import { API_CONFIG } from './config/api'
import { apiPost, apiGet } from '../shared/utils/apiClient'
import type {
  TransactionSentDto,
  UserVoteSummariesResponseDto,
  CreatedBoxesResponseDto,
  CreatedBoxesMetaDtoOrderByEnum,
  CreatedBoxesMetaDtoOrderDirectionEnum,
  AuthenticatedUserControllerGetUserVotesParamsOrderByEnum,
  BoxesControllerFindAllParamsOrderDirectionEnum,
  UserResponseDto,
  VoteSubmitResponseDto,
} from '../types/generated'

// Re-export types for convenience
export type {
  TransactionSentDto,
  UserVoteSummariesResponseDto,
  CreatedBoxesResponseDto,
  AuthenticatedUserControllerGetUserVotesParamsOrderByEnum,
  BoxesControllerFindAllParamsOrderDirectionEnum,
  CreatedBoxesMetaDtoOrderByEnum,
  CreatedBoxesMetaDtoOrderDirectionEnum,
}

export type UserVotesOrderBy =
  AuthenticatedUserControllerGetUserVotesParamsOrderByEnum

export type UserVotesOrderDirection =
  BoxesControllerFindAllParamsOrderDirectionEnum

export interface GetUserVotesParams {
  orderBy?: UserVotesOrderBy
  orderDirection?: UserVotesOrderDirection
  page?: number
  limit?: number
}

export type CreatedBoxesOrderBy = CreatedBoxesMetaDtoOrderByEnum
export type CreatedBoxesOrderDirection = CreatedBoxesMetaDtoOrderDirectionEnum

export interface GetCreatedBoxesParams {
  orderBy?: CreatedBoxesOrderBy
  orderDirection?: CreatedBoxesOrderDirection
  page?: number
  limit?: number
}

export const getUserVotes = async (
  params: GetUserVotesParams = {},
): Promise<UserVoteSummariesResponseDto> => {
  const queryParams = new URLSearchParams()

  if (params.orderBy) {
    queryParams.append('orderBy', params.orderBy)
  }

  if (params.orderDirection) {
    queryParams.append('orderDirection', params.orderDirection)
  }

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString())
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = queryString
    ? `${API_CONFIG.endpoints.users.activityVotes}?${queryString}`
    : API_CONFIG.endpoints.users.activityVotes

  const response = await apiGet(url)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch user votes: ${response.status} ${response.statusText}`,
    )
  }

  return response.json()
}

export const trackTransaction = async (
  transactionData: TransactionSentDto,
): Promise<VoteSubmitResponseDto> => {
  // Protected endpoint - requires authentication
  const response = await apiPost(
    API_CONFIG.endpoints.users.transactionSent,
    transactionData,
  )

  if (!response.ok) {
    throw new Error(
      `Failed to track transaction: ${response.status} ${response.statusText}`,
    )
  }

  return response.json()
}

export const getCreatedBoxes = async (
  params: GetCreatedBoxesParams = {},
): Promise<CreatedBoxesResponseDto> => {
  const queryParams = new URLSearchParams()

  if (params.orderBy) {
    queryParams.append('orderBy', params.orderBy)
  }

  if (params.orderDirection) {
    queryParams.append('orderDirection', params.orderDirection)
  }

  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString())
  }

  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = queryString
    ? `${API_CONFIG.endpoints.users.activityCreatedBoxes}?${queryString}`
    : API_CONFIG.endpoints.users.activityCreatedBoxes

  const response = await apiGet(url)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch created boxes: ${response.status} ${response.statusText}`,
    )
  }

  return response.json()
}

export const getUserProfile = async (): Promise<UserResponseDto> => {
  // Protected endpoint - requires authentication
  const response = await apiGet(API_CONFIG.endpoints.users.profile)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch user profile: ${response.status} ${response.statusText}`,
    )
  }

  return response.json()
}
